import React, { useMemo, useState } from 'react';
import {
  Box, Button, ButtonText, Center, Checkbox, CheckboxIcon, CheckboxIndicator, CheckboxLabel, Text, VStack,
} from '@gluestack-ui/themed';
import { CheckIcon } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

export type ConductStepProps = {
  onConfirm: () => void;
  isSubmitting?: boolean;
};

const CHECKBOX_KEYS = [
  'surroundings',
  'materials',
  'technicalMeans',
  'consequences',
] as const;

type ConductCheckboxKey = typeof CHECKBOX_KEYS[number];

/** Affirmative exam code-of-conduct screen (four required checkboxes). */
export function ConductStep({
  onConfirm,
  isSubmitting = false,
}: ConductStepProps) {
  const { t } = useTranslation();
  const [checked, setChecked] = useState<Record<ConductCheckboxKey, boolean>>({
    surroundings: false,
    materials: false,
    technicalMeans: false,
    consequences: false,
  });

  const allChecked = useMemo(
    () => CHECKBOX_KEYS.every((key) => checked[key]),
    [checked],
  );

  const setCheckedKey = (key: ConductCheckboxKey, selected: boolean) => {
    setChecked((prev) => ({ ...prev, [key]: selected }));
  };

  return (
    <Center flex={1} p="$6" testID="oral-exam-conduct-step">
      <VStack space="lg" maxWidth={560} width="100%" alignItems="center">
        <VStack space="sm" alignItems="center" width="100%">
          <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
            {t('oralExam.conduct.title')}
          </Text>
          <Text size="md" color="$textBody" textAlign="center">
            {t('oralExam.conduct.lead')}
          </Text>
        </VStack>

        <Box
          width="100%"
          bg="$bgSubtle"
          borderRadius="$lg"
          borderWidth={1}
          borderColor="$borderLight"
          p="$4"
          testID="oral-exam-conduct-body"
        >
          <VStack space="md">
            {CHECKBOX_KEYS.map((key) => (
              <Checkbox
                key={key}
                value={key}
                isChecked={checked[key]}
                onChange={(selected) => setCheckedKey(key, selected)}
                aria-label={t(`oralExam.conduct.items.${key}.title`)}
                testID={`oral-exam-conduct-checkbox-${key}`}
                alignItems="flex-start"
                sx={{ alignItems: 'flex-start' }}
              >
                <CheckboxIndicator mr="$3" mt="$0.5">
                  <CheckboxIcon as={CheckIcon} />
                </CheckboxIndicator>
                <CheckboxLabel flex={1} lineHeight="$lg">
                  <Text fontWeight="bold" color="$textHeading">
                    {t(`oralExam.conduct.items.${key}.title`)}
                  </Text>
                  {` — ${t(`oralExam.conduct.items.${key}.body`)}`}
                </CheckboxLabel>
              </Checkbox>
            ))}
          </VStack>
        </Box>

        <Button
          size="md"
          onPress={onConfirm}
          isDisabled={!allChecked || isSubmitting}
          testID="oral-exam-conduct-confirm"
          width="100%"
          maxWidth={280}
        >
          <ButtonText>{t('oralExam.conduct.confirm')}</ButtonText>
        </Button>
      </VStack>
    </Center>
  );
}
